<template>
    <div class="nopads">
        <!-- Header of collapsable item -->
        <div class="col-12 d-flex justify-content-between align-items-center pb-1">
            <div class="flex-grow-1">
                <h4
                    class="form-header pointer hover-header"
                    @click.stop="sensorReportVisible = !sensorReportVisible"
                >
                    {{ $t('contract_reports.sensor_device_reports.title') }}
                </h4>
            </div>
            <div
                v-if="loading"
                id="loader"
                class="spinner-section"
            />
            <div
                v-else
                class="ms-2 px-2"
                @click.stop="sensorReportVisible = !sensorReportVisible"
            >
                <font-awesome-icon
                    icon="arrow-down"
                    class="pointer no-focus arrow-icon dont-print hover-header"
                    :class="{ rotated: sensorReportVisible }"
                />
            </div>
        </div>
        <div
            class="routa-collapse"
            :class="{ collapsed: sensorReportVisible }"
        >
            <div
                v-if="results.length > 0"
                class="d-flex justify-content-end mb-2"
            >
                <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="csvLoading"
                    @click="downloadCsv"
                >
                    <font-awesome-icon icon="download" class="me-1" />
                    {{ $t('common.download_csv') }}
                </button>
            </div>
            <div
                v-if="results.length === 0"
                class="text-muted p-2"
            >
                {{ $t('common.no_results') }}
            </div>
            <div
                v-for="trip in results"
                :key="trip.trip_id"
                class="mb-4 p-3 bg-light-subtle rounded"
            >
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="fw-bold">{{ trip.vehicle.name }}</span>
                    <span
                        class="text-muted"
                        style="font-size: 0.85em"
                    >
                        {{ formatTime(trip.started_at) }} – {{ formatTime(trip.ended_at) }}
                    </span>
                </div>
                <div class="row">
                    <div
                        v-for="(sensor, i) in trip.sensors.filter(s => s.values.length > 0 && s.report_name)"
                        :key="sensor.id"
                        class="col-12 col-md-6 mb-3"
                    >
                        <div
                            class="fw-semibold mb-1"
                            style="font-size: 0.9em"
                        >
                            {{ sensor.display_name || sensor.name }} ({{ sensor.unit }})
                        </div>
                        <div style="height: 160px">
                            <Line
                                :data="getSensorChartData(sensor, i)"
                                :options="chartOptions"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <hr class="report-divider nopads">
    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin';
import { timeUtils } from '../mixins/TimeUtils';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale } from 'chart.js';
import { Line } from 'vue-chartjs';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);

const COLORS = ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1'];

export default {
    name: 'SensorDeviceReports',
    components: { Line },
    mixins: [restApi, timeUtils],
    props: {
        searchParams: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            sensorReportVisible: false,
            loading: false,
            csvLoading: false,
            results: [],
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true } },
                scales: { y: { beginAtZero: false } }
            }
        }
    },
    watch: {
        searchParams: {
            immediate: true,
            handler(val) {
                if (val) this.fetchData();
            }
        }
    },
    methods: {
        fetchData() {
            this.loading = true;
            this.restFetchParams(this.pavingMeasurementsReportUrl, this.searchParams, this.onSuccess, this.onError);
        },
        onSuccess(response) {
            this.loading = false;
            this.results = response.data;
        },
        onError() {
            this.loading = false;
        },
        formatTime(ts) {
            return ts ? new Date(ts).toLocaleString() : '-';
        },
        downloadCsv() {
            this.csvLoading = true;
            this.restGetDownloadFile(
                this.pavingMeasurementsCsvUrl,
                this.searchParams,
                this.downloadCsvSuccess,
                this.downloadCsvFailed
            );
        },
        downloadCsvSuccess(response) {
            this.csvLoading = false;
            
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            
            a.href = url;
            a.download = 'pavingmeasurements.zip';
            a.click();
            window.URL.revokeObjectURL(url);
        },
        downloadCsvFailed() {
            this.csvLoading = false;
        },
        getSensorChartData(sensor, colorIndex) {
            return {
                labels: sensor.values.map(v => new Date(v.recorded_at).toLocaleTimeString()),
                datasets: [{
                    label: sensor.display_name || sensor.name,
                    data: sensor.values.map(v => v.value),
                    borderColor: COLORS[colorIndex % COLORS.length],
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 2,
                }]
            }
        }
    }
}
</script>
